import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSave,
  faMapMarkerAlt,
  faImage,
  faCheckCircle,
  faSpinner,
  faTimes,
  faX,
  faHome,
  faDollarSign,
  faList,
  faFileContract,
  faShieldAlt,
  faCamera,
  faClock,
  faTrash,
  faPencil,
} from "@fortawesome/free-solid-svg-icons";
import "./property-settings.css";
import { useLanguage } from "../../../i18n/LanguageContext";
import PropertyMapPicker from "../../common/PropertyMapPicker";
import { getPropertyImageUrl } from "../../../utils/property-image";

function PropertySettings({ embeddedPropertyId, onBack } = {}) {
  const navigate = useNavigate();
  const { propertyId: routePropertyId } = useParams();
  // When rendered inside PropertyDashboard, use the passed prop; otherwise fall back to route param
  const propertyId = embeddedPropertyId || routePropertyId;
  const isEmbedded = !!embeddedPropertyId;
  const { t } = useLanguage();
  const token = localStorage.getItem("token");

  // State management
  const [activeTab, setActiveTab] = useState("basic");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "apartment",
    status: "active",
    price: "",
    bedrooms: "",
    bathrooms: "",
    area: "",
    furnishing: "unfurnished",
    amenities: [],
    images: [],
    thumbnail: null,
    address: "",
    state: "",
    district: "",
    municipality: "",
    coordinates: null,
    checkInTime: "14:00",
    checkOutTime: "10:00",
    minStay: "1",
    cancellationPolicy: "flexible",
    housRules: "",
    pets: false,
    smoking: false,
    visitorsPolicy: "allowed",
    verificationStatus: "unverified",
  });

  const [newImages, setNewImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState(new Set());
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);
  const [verificationForm, setVerificationForm] = useState({
    ownership_certificate: null,
    tax_clearance_certificate: null,
    land_deed_certificate: null,
    utility_bill_certificate: null,
    supportingNotes: "",
  });

  const AMENITIES = [
    { id: "wifi", label: "WiFi", icon: "📡" },
    { id: "kitchen", label: "Kitchen", icon: "🍳" },
    { id: "water", label: "Water", icon: "💧" },
    { id: "parking", label: "Parking", icon: "🅿️" },
    { id: "ac", label: "AC", icon: "❄️" },
    { id: "heating", label: "Heating", icon: "🔥" },
    { id: "electricity", label: "Electricity", icon: "⚡" },
    { id: "petfriendly", label: "Pet Friendly", icon: "🐾" },
    { id: "gym", label: "Gym", icon: "💪" },
    { id: "hotwater", label: "Hot Water", icon: "🚿" },
    { id: "garden", label: "Garden", icon: "🌳" },
    { id: "balcony", label: "Balcony", icon: "🪟" },
  ];

  // Fetch property data
  useEffect(() => {
    const fetchProperty = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/owner/properties/${propertyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch property");

        const data = await response.json();
        const normalizedVerificationStatus = String(data.verification_status || data.verificationStatus || "unverified").toLowerCase();
        
        const apiCoordinates =
          Number.isFinite(Number(data.lat)) && Number.isFinite(Number(data.lng))
            ? { lat: Number(data.lat), lng: Number(data.lng) }
            : Number.isFinite(Number(data.coordinates?.lat)) && Number.isFinite(Number(data.coordinates?.lng))
              ? { lat: Number(data.coordinates.lat), lng: Number(data.coordinates.lng) }
              : Number.isFinite(Number(data.location?.lat)) && Number.isFinite(Number(data.location?.lng))
                ? { lat: Number(data.location.lat), lng: Number(data.location.lng) }
                : null;

        // Map API data to form data
        setFormData({
          name: data.name || data.propertyName || "",
          description: data.description || "",
          type: data.type || data.propertyType || "apartment",
          status: data.status || "active",
          price: data.price ?? data.propertyPrice ?? "",
          bedrooms: data.bedrooms ?? data.propertyBedrooms ?? "",
          bathrooms: data.bathrooms ?? data.propertyBathrooms ?? "",
          area: data.area ?? data.propertyArea ?? data.sqft ?? "",
          furnishing: data.furnishing || "unfurnished",
          amenities: data.amenities || [],
          images: data.images || data.photos || [],
          thumbnail: data.thumbnail || null,
          address: data.address || data.propertyAddress || "",
          state: data.state || data.propertyState || "",
          district: data.district || data.propertyDistrict || "",
          municipality: data.municipality || data.propertyMunicipality || "",
          coordinates: apiCoordinates,
          checkInTime: data.checkInTime || "14:00",
          checkOutTime: data.checkOutTime || "10:00",
          minStay: String(data.minStay || "1"),
          cancellationPolicy: data.cancellationPolicy || "flexible",
          housRules: data.housRules || data.houseRules || "",
          pets: data.pets || false,
          smoking: data.smoking || false,
          visitorsPolicy: data.visitorsPolicy || "allowed",
          verificationStatus: normalizedVerificationStatus,
        });

        setLoading(false);
      } catch (err) {
        console.error("Error fetching property:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    if (propertyId && token) {
      fetchProperty();
    }
  }, [propertyId, token]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle amenity toggle
  const toggleAmenity = (amenityId) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter((a) => a !== amenityId)
        : [...prev.amenities, amenityId],
    }));
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setNewImages((prev) => [...prev, ...files.map((f) => ({ file: f, preview: URL.createObjectURL(f) }))]);
  };

  // Handle map selection
  const handleMapSelect = (location) => {
    const lat = location?.coordinates?.lat ?? location?.lat ?? formData.coordinates?.lat;
    const lng = location?.coordinates?.lng ?? location?.lng ?? formData.coordinates?.lng;
    setFormData((prev) => ({
      ...prev,
      coordinates: { lat, lng },
      address: location?.address || prev.address,
      municipality: location?.municipality || prev.municipality,
      district: location?.district || prev.district,
      state: location?.state || prev.state,
    }));
    setShowMapPicker(false);
  };

  // Mark image for deletion
  const markImageForDeletion = (imageId) => {
    setImagesToDelete((prev) => new Set([...prev, imageId]));
  };

  // Remove marked image
  const restoreImage = (imageId) => {
    const newSet = new Set(imagesToDelete);
    newSet.delete(imageId);
    setImagesToDelete(newSet);
  };

  // Remove new image before upload
  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Save property
  const handleSaveProperty = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      // Upload new images if any
      if (newImages.length > 0) {
        setUploadingImages(true);
        const uploadFormData = new FormData();
        newImages.forEach((img) => {
          uploadFormData.append("images", img.file);
        });

        const uploadResponse = await fetch(`/api/properties/${propertyId}/images`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: uploadFormData,
        });

        if (!uploadResponse.ok) {
          throw new Error("Failed to upload images");
        }

        setNewImages([]);
        setUploadingImages(false);
      }

      // Delete marked images
      if (imagesToDelete.size > 0) {
        const idsToDelete = Array.from(imagesToDelete);
        const deleteResponses = await Promise.all(
          idsToDelete.map((imageId) =>
            fetch(`/api/properties/${propertyId}/images/${encodeURIComponent(imageId)}`, {
              method: "DELETE",
              headers: { Authorization: `Bearer ${token}` },
            })
          )
        );

        if (deleteResponses.some((res) => !res.ok)) {
          throw new Error("Failed to delete images");
        }

        setImagesToDelete(new Set());
      }

      // Update property data
      const updateResponse = await fetch(`/api/properties/${propertyId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          type: formData.type,
          status: formData.status,
          price: formData.price,
          bedrooms: formData.bedrooms,
          bathrooms: formData.bathrooms,
          area: formData.area,
          furnishing: formData.furnishing,
          amenities: formData.amenities,
          address: formData.address,
          state: formData.state,
          district: formData.district,
          municipality: formData.municipality,
          coordinates: formData.coordinates,
          checkInTime: formData.checkInTime,
          checkOutTime: formData.checkOutTime,
          minStay: formData.minStay,
          cancellationPolicy: formData.cancellationPolicy,
          housRules: formData.housRules,
          pets: formData.pets,
          smoking: formData.smoking,
          visitorsPolicy: formData.visitorsPolicy,
        }),
      });

      if (!updateResponse.ok) {
        throw new Error("Failed to update property");
      }

      setSuccess("Property settings saved successfully!");
      setTimeout(() => {
        navigate("/owner-dashboard");
      }, 2000);
    } catch (err) {
      console.error("Error saving property:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleVerificationFileChange = (field, file) => {
    setVerificationForm((prev) => ({
      ...prev,
      [field]: file || null,
    }));
  };

  const refreshVerificationStatus = async () => {
    try {
      const response = await fetch(`/api/owner/properties/${propertyId}/verification-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Unable to load verification status.");
      }

      const normalizedStatus = String(data.status || "unverified").toLowerCase();
      setFormData((prev) => ({
        ...prev,
        verificationStatus: normalizedStatus,
      }));
      setSuccess(t("propertySettings.verificationStatusUpdated") || "Verification status updated.");
    } catch (err) {
      setError(err.message || "Unable to check verification status.");
    }
  };

  const submitPropertyVerification = async () => {
    if (!verificationForm.ownership_certificate || !verificationForm.tax_clearance_certificate) {
      setError(t("propertySettings.verificationRequiredDocsError") || "Please upload ownership certificate and tax/ward clearance documents.");
      return;
    }

    try {
      setVerificationSubmitting(true);
      setError("");
      setSuccess("");

      const submitFormData = new FormData();
      submitFormData.append("ownership_certificate", verificationForm.ownership_certificate);
      submitFormData.append("tax_clearance_certificate", verificationForm.tax_clearance_certificate);
      if (verificationForm.land_deed_certificate) {
        submitFormData.append("land_deed_certificate", verificationForm.land_deed_certificate);
      }
      if (verificationForm.utility_bill_certificate) {
        submitFormData.append("utility_bill_certificate", verificationForm.utility_bill_certificate);
      }
      if (verificationForm.supportingNotes.trim()) {
        submitFormData.append("supportingNotes", verificationForm.supportingNotes.trim());
      }

      const response = await fetch(`/api/owner/properties/${propertyId}/verification-request`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: submitFormData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Unable to submit verification request.");
      }

      setFormData((prev) => ({
        ...prev,
        verificationStatus: "pending",
      }));
      setVerificationForm({
        ownership_certificate: null,
        tax_clearance_certificate: null,
        land_deed_certificate: null,
        utility_bill_certificate: null,
        supportingNotes: "",
      });
      setSuccess(data.message || (t("propertySettings.verificationSubmitted") || "Verification request submitted successfully."));
    } catch (err) {
      setError(err.message || "Unable to submit verification request.");
    } finally {
      setVerificationSubmitting(false);
    }
  };

  const verificationStatusTitle =
    formData.verificationStatus === "approved"
      ? t("propertySettings.verificationStatusApproved") || "Verified"
      : formData.verificationStatus === "pending"
        ? t("propertySettings.verificationStatusPending") || "Verification Pending"
        : formData.verificationStatus === "rejected"
          ? t("propertySettings.verificationStatusRejected") || "Verification Rejected"
          : t("propertySettings.verificationStatusUnverified") || "Not Verified";

  const verificationStatusDescription =
    formData.verificationStatus === "approved"
      ? t("propertySettings.verificationApprovedDesc") || "Your property has been verified. You have the verified badge displayed on your listings."
      : formData.verificationStatus === "pending"
        ? t("propertySettings.verificationPendingDesc") || "Your verification request is being reviewed. This usually takes 1-2 business days."
        : formData.verificationStatus === "rejected"
          ? t("propertySettings.verificationRejectedDesc") || "Your verification was rejected. You can resubmit documents."
          : t("propertySettings.verificationUnverifiedDesc") || "Submit proof documents to get your property verified.";

    const hasCoordinates = Number.isFinite(Number(formData.coordinates?.lat)) && Number.isFinite(Number(formData.coordinates?.lng));
    const mapPreviewSrc = hasCoordinates
      ? `https://maps.google.com/maps?q=${encodeURIComponent(`${Number(formData.coordinates.lat)},${Number(formData.coordinates.lng)}`)}&z=16&output=embed`
      : "";

  if (loading) {
    return (
      <div className="ps-loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="ps-spinner" />
        <p>{t("common.loading") || "Loading..."}</p>
      </div>
    );
  }

  return (
    <div className={isEmbedded ? "ps-embedded-shell" : "property-settings-shell"}>
      {/* Header – hidden when embedded (dashboard owns the header) */}
      {!isEmbedded && (
      <div className="ps-header">
        <div className="ps-header-top">
          <button className="ps-back-btn" onClick={() => onBack ? onBack() : navigate("/owner-dashboard")}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>{t("common.back") || "Back"}</span>
          </button>
          <div className="ps-header-title">
            <h1>{formData.name || "Property Settings"}</h1>
            <p className="ps-subtitle">{t("propertySettings.manageDetails") || "Manage all your property details"}</p>
          </div>
          <button className="ps-save-btn" onClick={handleSaveProperty} disabled={saving || uploadingImages}>
            <FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} />
            <span>{saving ? "Saving..." : "Save Changes"}</span>
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="ps-alert ps-alert--error">
            <span>{error}</span>
            <button onClick={() => setError("")}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}
        {success && (
          <div className="ps-alert ps-alert--success">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>{success}</span>
          </div>
        )}
      </div>
      )} {/* end !isEmbedded header */}

      {/* When embedded, show inline alerts + save button at the top */}
      {isEmbedded && (
        <div className="ps-embedded-toolbar">
          {error && (
            <div className="ps-alert ps-alert--error">
              <span>{error}</span>
              <button onClick={() => setError("")}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
          )}
          {success && (
            <div className="ps-alert ps-alert--success">
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>{success}</span>
            </div>
          )}
          <button className="ps-save-btn" onClick={handleSaveProperty} disabled={saving || uploadingImages}>
            <FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} />
            <span>{saving ? "Saving..." : "Save Changes"}</span>
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="ps-tabs">
        <button
          className={`ps-tab ${activeTab === "basic" ? "ps-tab--active" : ""}`}
          onClick={() => setActiveTab("basic")}
        >
          <FontAwesomeIcon icon={faHome} />
          <span>{t("propertySettings.basicInfo") || "Basic Info"}</span>
        </button>
        <button
          className={`ps-tab ${activeTab === "location" ? "ps-tab--active" : ""}`}
          onClick={() => setActiveTab("location")}
        >
          <FontAwesomeIcon icon={faMapMarkerAlt} />
          <span>{t("propertySettings.location") || "Location"}</span>
        </button>
        <button
          className={`ps-tab ${activeTab === "pricing" ? "ps-tab--active" : ""}`}
          onClick={() => setActiveTab("pricing")}
        >
          <FontAwesomeIcon icon={faDollarSign} />
          <span>{t("propertySettings.pricing") || "Pricing"}</span>
        </button>
        <button
          className={`ps-tab ${activeTab === "amenities" ? "ps-tab--active" : ""}`}
          onClick={() => setActiveTab("amenities")}
        >
          <FontAwesomeIcon icon={faList} />
          <span>{t("propertySettings.amenities") || "Amenities"}</span>
        </button>
        <button
          className={`ps-tab ${activeTab === "images" ? "ps-tab--active" : ""}`}
          onClick={() => setActiveTab("images")}
        >
          <FontAwesomeIcon icon={faImage} />
          <span>{t("propertySettings.images") || "Images"}</span>
        </button>
        <button
          className={`ps-tab ${activeTab === "policies" ? "ps-tab--active" : ""}`}
          onClick={() => setActiveTab("policies")}
        >
          <FontAwesomeIcon icon={faFileContract} />
          <span>{t("propertySettings.policies") || "Policies"}</span>
        </button>
        <button
          className={`ps-tab ${activeTab === "verification" ? "ps-tab--active" : ""}`}
          onClick={() => setActiveTab("verification")}
        >
          <FontAwesomeIcon icon={faShieldAlt} />
          <span>{t("propertySettings.verification") || "Verification"}</span>
        </button>
      </div>

      {/* Content Area */}
      <div className="ps-content">
        {/* BASIC INFO TAB */}
        {activeTab === "basic" && (
          <div className="ps-section">
            <div className="ps-section-header">
              <h2>{t("propertySettings.basicInfo") || "Basic Information"}</h2>
              <p>{t("propertySettings.basicInfoDesc") || "Essential details about your property"}</p>
            </div>

            <div className="ps-form-grid">
              {/* Property Name */}
              <div className="ps-form-group ps-form-group--full">
                <label>{t("propertySettings.propertyName")}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Cozy Apartment in Thamel"
                  className="ps-input"
                />
              </div>

              {/* Description */}
              <div className="ps-form-group ps-form-group--full">
                <label>{t("propertySettings.description")}</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Tell renters about your property..."
                  className="ps-textarea"
                  rows="5"
                />
              </div>

              {/* Property Type */}
              <div className="ps-form-group">
                <label>{t("propertySettings.propertyType")}</label>
                <select name="type" value={formData.type} onChange={handleInputChange} className="ps-select">
                  <option value="house">House</option>
                  <option value="apartment">Apartment</option>
                  <option value="villa">Villa</option>
                  <option value="studio">Studio</option>
                  <option value="room">Room</option>
                  <option value="land">Land</option>
                  <option value="commercial">Commercial</option>
                </select>
              </div>

              {/* Status */}
              <div className="ps-form-group">
                <label>{t("propertySettings.status")}</label>
                <select name="status" value={formData.status} onChange={handleInputChange} className="ps-select">
                  <option value="active">{t("propertySettings.statusActive")}</option>
                  <option value="inactive">{t("propertySettings.statusInactive")}</option>
                  <option value="maintenance">{t("propertySettings.statusMaintenance")}</option>
                </select>
              </div>

              {/* Bedrooms */}
              <div className="ps-form-group">
                <label>{t("propertySettings.bedrooms")}</label>
                <input
                  type="number"
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={handleInputChange}
                  min="0"
                  className="ps-input"
                />
              </div>

              {/* Bathrooms */}
              <div className="ps-form-group">
                <label>{t("propertySettings.bathrooms")}</label>
                <input
                  type="number"
                  name="bathrooms"
                  value={formData.bathrooms}
                  onChange={handleInputChange}
                  min="0"
                  className="ps-input"
                />
              </div>

              {/* Area */}
              <div className="ps-form-group">
                <label>{t("propertySettings.area")}</label>
                <input
                  type="number"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  min="0"
                  className="ps-input"
                />
              </div>

              {/* Furnishing */}
              <div className="ps-form-group">
                <label>{t("propertySettings.furnishing")}</label>
                <select name="furnishing" value={formData.furnishing} onChange={handleInputChange} className="ps-select">
                  <option value="unfurnished">अफर्निश्ड / Unfurnished</option>
                  <option value="semifurnished">अर्ध-सज्जित / Semi-Furnished</option>
                  <option value="furnished">सज्जित / Furnished</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* LOCATION TAB */}
        {activeTab === "location" && (
          <div className="ps-section">
            <div className="ps-section-header">
              <h2>{t("propertySettings.location") || "Location"}</h2>
              <p>{t("propertySettings.locationDesc") || "Pick your property location on the map"}</p>
            </div>

            <div className="ps-location-container">
              <button className="ps-map-picker-btn" onClick={() => setShowMapPicker(true)}>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>{t("propertySettings.pickFromMap")}</span>
              </button>

              {showMapPicker && (
                <div className="ps-map-modal-overlay" onClick={() => setShowMapPicker(false)}>
                  <div className="ps-map-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="ps-map-modal-header">
                      <h3>{t("propertySettings.selectLocation")}</h3>
                      <button onClick={() => setShowMapPicker(false)}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                    <PropertyMapPicker
                      lat={formData.coordinates?.lat}
                      lng={formData.coordinates?.lng}
                      propertyType={formData.type}
                      onChange={handleMapSelect}
                    />
                  </div>
                </div>
              )}

              <div className="ps-location-info">
                {formData.coordinates && (
                  <>
                    <div className="ps-location-display">
                      <strong>{t("propertySettings.selectedLocation")}</strong>
                      <p>{formData.address}</p>
                      <p className="ps-coordinates">
                        Lat: {formData.coordinates.lat?.toFixed(4)}, Lng: {formData.coordinates.lng?.toFixed(4)}
                      </p>

                      {hasCoordinates && (
                        <div className="ps-map-preview-wrap">
                          <div className="ps-map-preview-header">
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                            <span>{t("propertySettings.mapPreview", "Map Preview")}</span>
                          </div>
                          <div className="ps-map-preview-frame-shell">
                            <iframe
                              title={t("propertySettings.mapPreview", "Map Preview")}
                              src={mapPreviewSrc}
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                              className="ps-map-preview-frame"
                            />
                          </div>
                          <small className="ps-map-preview-note">
                            {t("propertySettings.mapPreviewHint", "This preview updates after you pick/drag a location on the map.")}
                          </small>
                        </div>
                      )}
                    </div>

                    <div className="ps-form-grid">
                      <div className="ps-form-group">
                        <label>{t("propertySettings.state")}</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          className="ps-input"
                        />
                      </div>

                      <div className="ps-form-group">
                        <label>{t("propertySettings.district")}</label>
                        <input
                          type="text"
                          name="district"
                          value={formData.district}
                          onChange={handleInputChange}
                          className="ps-input"
                        />
                      </div>

                      <div className="ps-form-group">
                        <label>{t("propertySettings.municipality")}</label>
                        <input
                          type="text"
                          name="municipality"
                          value={formData.municipality}
                          onChange={handleInputChange}
                          className="ps-input"
                        />
                      </div>

                      <div className="ps-form-group ps-form-group--full">
                        <label>{t("propertySettings.address")}</label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          className="ps-input"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PRICING TAB */}
        {activeTab === "pricing" && (
          <div className="ps-section">
            <div className="ps-section-header">
              <h2>{t("propertySettings.pricing") || "Pricing & Stays"}</h2>
              <p>{t("propertySettings.pricingDesc") || "Set rental rates and stay policies"}</p>
            </div>

            <div className="ps-form-grid">
              {/* Monthly Rent */}
              <div className="ps-form-group">
                <label>{t("propertySettings.monthlyRent")}</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="0"
                  className="ps-input"
                />
              </div>

              {/* Minimum Stay */}
              <div className="ps-form-group">
                <label>{t("propertySettings.minimumStay")}</label>
                <input
                  type="number"
                  name="minStay"
                  value={formData.minStay}
                  onChange={handleInputChange}
                  min="1"
                  className="ps-input"
                />
              </div>

              {/* Check-in Time */}
              <div className="ps-form-group">
                <label>{t("propertySettings.checkInTime")}</label>
                <input
                  type="time"
                  name="checkInTime"
                  value={formData.checkInTime}
                  onChange={handleInputChange}
                  className="ps-input"
                />
              </div>

              {/* Check-out Time */}
              <div className="ps-form-group">
                <label>{t("propertySettings.checkOutTime")}</label>
                <input
                  type="time"
                  name="checkOutTime"
                  value={formData.checkOutTime}
                  onChange={handleInputChange}
                  className="ps-input"
                />
              </div>

              {/* Cancellation Policy */}
              <div className="ps-form-group ps-form-group--full">
                <label>{t("propertySettings.cancellationPolicy") || "Cancellation Policy"}</label>
                <select
                  name="cancellationPolicy"
                  value={formData.cancellationPolicy}
                  onChange={handleInputChange}
                  className="ps-select"
                >
                  <option value="strict">{t("propertySettings.policyStrict") || "Strict (Full refund 7 days before)"}</option>
                  <option value="moderate">{t("propertySettings.policyModerate") || "Moderate (50% refund 3 days before)"}</option>
                  <option value="flexible">{t("propertySettings.policyFlexible") || "Flexible (Full refund anytime)"}</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* AMENITIES TAB */}
        {activeTab === "amenities" && (
          <div className="ps-section">
            <div className="ps-section-header">
              <h2>{t("propertySettings.amenities") || "Amenities"}</h2>
              <p>{t("propertySettings.amenitiesDesc") || "Select all amenities available at your property"}</p>
            </div>

            <div className="ps-amenities-grid">
              {AMENITIES.map((amenity) => (
                <label key={amenity.id} className={`ps-amenity-card ${formData.amenities.includes(amenity.id) ? "ps-amenity-card--checked" : ""}`}>
                  <input
                    type="checkbox"
                    checked={formData.amenities.includes(amenity.id)}
                    onChange={() => toggleAmenity(amenity.id)}
                    className="ps-amenity-input"
                  />
                  <div className="ps-amenity-content">
                    <span className="ps-amenity-icon">{amenity.icon}</span>
                    <span className="ps-amenity-label">{amenity.label}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* IMAGES TAB */}
        {activeTab === "images" && (
          <div className="ps-section">
            <div className="ps-section-header">
              <h2>{t("propertySettings.images") || "Images & Gallery"}</h2>
              <p>{t("propertySettings.imagesDesc") || "Upload and manage property photos"}</p>
            </div>

            {/* Image Upload Area */}
            <div className="ps-upload-area">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="ps-file-input"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="ps-upload-label">
                <FontAwesomeIcon icon={faCamera} className="ps-upload-icon" />
                <div>
                  <p className="ps-upload-main">{t("propertySettings.dragOrClick") || "Drag images here or click to upload"}</p>
                  <p className="ps-upload-sub">{t("propertySettings.supportedFormats") || "Supports: JPG, PNG, WebP"}</p>
                </div>
              </label>
            </div>

            {/* New Images Preview */}
            {newImages.length > 0 && (
              <div className="ps-image-section">
                <h4>{t("propertySettings.newImages") || "New Images to Upload"} ({newImages.length})</h4>
                <div className="ps-images-grid">
                  {newImages.map((img, idx) => (
                    <div key={idx} className="ps-image-card ps-image-card--new">
                      <img src={img.preview} alt={`New ${idx}`} />
                      <button className="ps-image-delete-btn" onClick={() => removeNewImage(idx)}>
                        <FontAwesomeIcon icon={faX} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Existing Images */}
            {formData.images && formData.images.length > 0 && (
              <div className="ps-image-section">
                <h4>{t("propertySettings.existingImages") || "Existing Images"} ({formData.images.filter((img) => !imagesToDelete.has(img._id || img)).length})</h4>
                <div className="ps-images-grid">
                  {formData.images.map((img) => {
                    const imgId = img._id || img.id || img;
                    const isMarkedForDelete = imagesToDelete.has(imgId);

                    return (
                      <div key={imgId} className={`ps-image-card ${isMarkedForDelete ? "ps-image-card--deleted" : ""}`}>
                        <img
                          src={typeof img === "string" ? getPropertyImageUrl(img) : img.url || img.image}
                          alt="Property"
                        />
                        {isMarkedForDelete && <div className="ps-image-overlay">{t("propertySettings.markedForDelete") || "Marked for Deletion"}</div>}
                        <button
                          className="ps-image-action-btn"
                          onClick={() => (isMarkedForDelete ? restoreImage(imgId) : markImageForDeletion(imgId))}
                        >
                          <FontAwesomeIcon icon={isMarkedForDelete ? faPencil : faTrash} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* POLICIES TAB */}
        {activeTab === "policies" && (
          <div className="ps-section">
            <div className="ps-section-header">
              <h2>{t("propertySettings.policies") || "Rules & Policies"}</h2>
              <p>{t("propertySettings.policiesDesc") || "Set house rules and guest policies"}</p>
            </div>

            <div className="ps-form-grid">
              {/* House Rules */}
              <div className="ps-form-group ps-form-group--full">
                <label>{t("propertySettings.houseRules") || "House Rules"}</label>
                <textarea
                  name="housRules"
                  value={formData.housRules}
                  onChange={handleInputChange}
                  placeholder="e.g., No loud noise after 10 PM, Keep property clean, etc."
                  className="ps-textarea"
                  rows="6"
                />
              </div>

              {/* Pets */}
              <div className="ps-form-group">
                <label className="ps-checkbox-label">
                  <input
                    type="checkbox"
                    name="pets"
                    checked={formData.pets}
                    onChange={handleInputChange}
                    className="ps-checkbox"
                  />
                  <span>{t("propertySettings.petsAllowed")}</span>
                </label>
              </div>

              {/* Smoking */}
              <div className="ps-form-group">
                <label className="ps-checkbox-label">
                  <input
                    type="checkbox"
                    name="smoking"
                    checked={formData.smoking}
                    onChange={handleInputChange}
                    className="ps-checkbox"
                  />
                  <span>{t("propertySettings.smokingAllowed")}</span>
                </label>
              </div>

              {/* Visitors Policy */}
              <div className="ps-form-group ps-form-group--full">
                <label>{t("propertySettings.visitorsPolicy")}</label>
                <select
                  name="visitorsPolicy"
                  value={formData.visitorsPolicy}
                  onChange={handleInputChange}
                  className="ps-select"
                >
                  <option value="allowed">{t("propertySettings.visitorsAllowed")}</option>
                  <option value="restricted">{t("propertySettings.visitorsRestricted")}</option>
                  <option value="notallowed">{t("propertySettings.visitorsNotAllowed")}</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* VERIFICATION TAB */}
        {activeTab === "verification" && (
          <div className="ps-section">
            <div className="ps-section-header">
              <h2>{t("propertySettings.verificationTitle") || "Property Verification"}</h2>
              <p>{t("propertySettings.verificationDesc") || "Boost renter trust with verified badge"}</p>
            </div>

            <div className="ps-verification-container">
              <div className="ps-verification-status">
                <div className={`ps-verification-badge ps-verification-badge--${formData.verificationStatus}`}>
                  <FontAwesomeIcon 
                    icon={formData.verificationStatus === "approved" ? faCheckCircle : formData.verificationStatus === "pending" ? faClock : faShieldAlt} 
                  />
                  <div>
                    <strong>{verificationStatusTitle}</strong>
                    <p>{verificationStatusDescription}</p>
                  </div>
                </div>
              </div>

              <div className="ps-verification-benefits">
                <h4>{t("propertySettings.verificationBenefitsTitle") || "Benefits of Verification"}</h4>
                <ul>
                  <li>{t("propertySettings.verificationBenefit1") || "✓ Build trust with renters"}</li>
                  <li>{t("propertySettings.verificationBenefit2") || "✓ Increase property visibility"}</li>
                  <li>{t("propertySettings.verificationBenefit3") || "✓ Get verified badge on listings"}</li>
                  <li>{t("propertySettings.verificationBenefit4") || "✓ Reduce fraudulent inquiries"}</li>
                  <li>{t("propertySettings.verificationBenefit5") || "✓ Higher chance of property rental"}</li>
                </ul>
              </div>

              <div className="ps-verification-action">
                {formData.verificationStatus !== "approved" && formData.verificationStatus !== "pending" && (
                  <div className="ps-form-grid">
                    <div className="ps-form-group">
                      <label>{t("propertySettings.verificationOwnershipDoc") || "Ownership Certificate"} *</label>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        className="ps-input"
                        onChange={(e) => handleVerificationFileChange("ownership_certificate", e.target.files?.[0])}
                      />
                    </div>
                    <div className="ps-form-group">
                      <label>{t("propertySettings.verificationTaxDoc") || "Tax/Ward Clearance Certificate"} *</label>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        className="ps-input"
                        onChange={(e) => handleVerificationFileChange("tax_clearance_certificate", e.target.files?.[0])}
                      />
                    </div>
                    <div className="ps-form-group">
                      <label>{t("propertySettings.verificationLandDoc") || "Land Deed (Optional)"}</label>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        className="ps-input"
                        onChange={(e) => handleVerificationFileChange("land_deed_certificate", e.target.files?.[0])}
                      />
                    </div>
                    <div className="ps-form-group">
                      <label>{t("propertySettings.verificationUtilityDoc") || "Utility Bill (Optional)"}</label>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        className="ps-input"
                        onChange={(e) => handleVerificationFileChange("utility_bill_certificate", e.target.files?.[0])}
                      />
                    </div>
                    <div className="ps-form-group ps-form-group--full">
                      <label>{t("propertySettings.verificationNotes") || "Supporting Notes (Optional)"}</label>
                      <textarea
                        className="ps-textarea"
                        rows="3"
                        value={verificationForm.supportingNotes}
                        onChange={(e) => setVerificationForm((prev) => ({ ...prev, supportingNotes: e.target.value }))}
                        placeholder={t("propertySettings.verificationNotesPlaceholder") || "Add context for admin review"}
                      />
                    </div>
                  </div>
                )}

                {formData.verificationStatus !== "approved" && (
                  <button
                    className="ps-verify-btn"
                    disabled={verificationSubmitting}
                    onClick={formData.verificationStatus === "pending" ? refreshVerificationStatus : submitPropertyVerification}
                  >
                    <FontAwesomeIcon icon={verificationSubmitting ? faSpinner : faShieldAlt} spin={verificationSubmitting} />
                    {formData.verificationStatus === "pending"
                      ? t("propertySettings.verificationCtaPending") || "Check Verification Status"
                      : (verificationSubmitting
                        ? t("propertySettings.verificationSubmitting") || "Submitting..."
                        : t("propertySettings.verificationCtaSubmit") || "Submit Verification Documents")}
                  </button>
                )}
                {formData.verificationStatus === "approved" && (
                  <div className="ps-verified-info">
                    <FontAwesomeIcon icon={faCheckCircle} className="ps-verified-icon" />
                    <p>{t("propertySettings.verificationApprovedInfo") || "Your property is verified and will display the badge on all your listings."}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PropertySettings;
